/**
 * Generic "things falling down the screen" particle engine, shared by the snow,
 * petal, leaf, clover, coin, … effects. Each particle falls at its own speed
 * with a horizontal drift, an optional side-to-side sway, and an optional spin;
 * a per-effect sprite function draws the shape. Kept out of React so the
 * component stays a thin canvas host.
 */

/** A single falling particle's position and motion. */
export interface FallingParticle {
  x: number;
  y: number;
  vy: number; // fall speed
  vx: number; // constant horizontal drift
  size: number;
  alpha: number;
  sway: number; // horizontal sway amplitude
  swayPhase: number;
  swayFreq: number;
  rot: number; // current rotation (radians)
  rotSpeed: number;
  colorIndex: number; // index into the effect's fill palette (0 when single-color)
}

type Range = [number, number];

/** Per-effect tuning. Ranges are sampled per particle. */
export interface FallingConfig {
  count: number;
  size: Range;
  speed: Range; // vy
  drift?: Range; // vx, default [0, 0]
  sway?: Range; // amplitude, default [0, 0]
  swayFreq?: Range; // default [0.01, 0.03]
  spin?: Range; // rotation speed, default [0, 0]
  alpha?: Range; // default [1, 1]
}

const rand = (min: number, max: number): number => min + Math.random() * (max - min);
const pick = (r: Range | undefined, fallback: Range): number =>
  rand(...(r ?? fallback));

/** Build `count` particles randomly distributed across the field. */
export function createFallingParticles(
  cfg: FallingConfig,
  width: number,
  height: number,
  colorCount = 1
): FallingParticle[] {
  return Array.from({ length: cfg.count }, () => ({
    colorIndex: Math.floor(Math.random() * colorCount),
    x: rand(0, width),
    y: rand(0, height),
    vy: pick(cfg.speed, cfg.speed),
    vx: pick(cfg.drift, [0, 0]),
    size: pick(cfg.size, cfg.size),
    alpha: pick(cfg.alpha, [1, 1]),
    sway: pick(cfg.sway, [0, 0]),
    swayPhase: rand(0, Math.PI * 2),
    swayFreq: pick(cfg.swayFreq, [0.01, 0.03]),
    rot: rand(0, Math.PI * 2),
    rotSpeed: pick(cfg.spin, [0, 0]),
  }));
}

/**
 * Advance and paint every particle one frame, respawning any that fall off the
 * bottom. `drawSprite(ctx, size)` draws one sprite centered at the origin (the
 * engine handles translate/rotate/alpha and sets the fill). Mutates in place.
 */
export function drawFallingParticles(
  ctx: CanvasRenderingContext2D,
  particles: FallingParticle[],
  width: number,
  height: number,
  fills: string[],
  drawSprite: (ctx: CanvasRenderingContext2D, size: number) => void
): void {
  ctx.clearRect(0, 0, width, height);
  for (const p of particles) {
    ctx.fillStyle = fills[p.colorIndex] ?? fills[0] ?? '#ffffff';
    p.y += p.vy;
    p.x += p.vx;
    p.swayPhase += p.swayFreq;
    p.rot += p.rotSpeed;
    if (p.y - p.size > height) {
      p.y = -p.size;
      p.x = rand(0, width);
    }
    if (p.x < -p.size) p.x = width + p.size;
    else if (p.x > width + p.size) p.x = -p.size;

    ctx.save();
    ctx.globalAlpha = p.alpha;
    ctx.translate(p.x + Math.sin(p.swayPhase) * p.sway, p.y);
    ctx.rotate(p.rot);
    drawSprite(ctx, p.size);
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}
